import { Dispatch, ForwardedRef, SetStateAction, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../constants";
import { Origin, PublicDevelopmentManagerDetailsPreviewParams } from "../../../../models";
import { logError } from "../../../../service/error";
import {
  getOrganisationListingPreview,
  saveOrganisationListingPreview,
  updateDevelopmentManagerListing,
} from "../../../../service/organisation";
import { getDevelopmentManagerDetails } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../utils";
import { recursivelyRemoveEmptyStrings } from "../../../../utils/rest";
import { getPublicDevelopmentManagerDetailsPreviewRoute } from "../../../../utils/routes";
import {
  FormAboutUsData,
  FormAtAGlanceData,
  FormHeroData,
  FormNames,
  FormOurServicesData,
  MarketingAssetsFormChangedAction,
  MarketingAssetsSubmitHandler,
  UpdateListingType,
} from "../../../shared";

interface UseMarketingAssetsReturnData {
  heroFormRef: ForwardedRef<MarketingAssetsSubmitHandler>;
  atAGlanceFormRef: ForwardedRef<MarketingAssetsSubmitHandler>;
  ourServicesFormRef: ForwardedRef<MarketingAssetsSubmitHandler>;
  aboutUsFormRef: ForwardedRef<MarketingAssetsSubmitHandler>;
  dataIsLoading: boolean;
  hasUnsavedChanges: MarketingAssetsHasUnsavedChanges;
  listingRowVersion: number;
  setListingRowVersion: Dispatch<SetStateAction<number>>;
  dispatch: Dispatch<MarketingAssetsFormChangedAction>;
  heroDefaultValues: FormHeroData | undefined;
  atAGlanceDefaultValues: FormAtAGlanceData | undefined;
  ourServicesDefaultValues: FormOurServicesData | undefined;
  aboutUsDefaultValues: FormAboutUsData | undefined;
  heroOldDefaultValues: FormHeroData | undefined;
  atAGlanceOldDefaultValues: FormAtAGlanceData | undefined;
  ourServicesOldDefaultValues: FormOurServicesData | undefined;
  aboutUsOldDefaultValues: FormAboutUsData | undefined;
  isHandlePreviewLoading: boolean;
  handlePreview: () => void;
  previewMode: boolean;
  getCurrentFormsData: (formWhichWasSaved: string) => string;
  hasPermission: (permission: string) => boolean;
  objectUuid: string;
  objectType: string;
  objectKey: string;
  updateListing: UpdateListingType;
}

interface MarketingAssetsHasUnsavedChanges {
  hero: boolean;
  atAGlance: boolean;
  ourServices: boolean;
  aboutUs: boolean;
}

const reducer = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prevState: any,
  action: MarketingAssetsFormChangedAction
): MarketingAssetsHasUnsavedChanges => {
  switch (action.type) {
    case "SET_HERO_FORM_CHANGED": {
      return {
        ...prevState,
        hero: action.value,
      };
    }
    case "SET_AT_A_GLANCE_FORM_CHANGED": {
      return {
        ...prevState,
        atAGlance: action.value,
      };
    }
    case "SET_OUR_SERVICES_FORM_CHANGED": {
      return {
        ...prevState,
        ourServices: action.value,
      };
    }
    case "SET_ABOUT_US_FORM_CHANGED": {
      return {
        ...prevState,
        aboutUs: action.value,
      };
    }

    default: {
      logError({ error: `Could not determine an action type for action: ${action}` });
      return {
        // unrecognized dispatch action
        ...prevState,
      };
    }
  }
};

export const useMarketingAssets = (): UseMarketingAssetsReturnData => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { previewUuid } = useParams<PublicDevelopmentManagerDetailsPreviewParams>();
  const { currentDevelopmentManagerUuid, hasPermission } = useAuth();

  const [hasUnsavedChanges, dispatch] = useReducer(reducer, {
    hero: false,
    atAGlance: false,
    ourServices: false,
    aboutUs: false,
  });

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [listingRowVersion, setListingRowVersion] = useState<number>(1);

  const [heroDefaultValues, setHeroDefaultValues] = useState<FormHeroData | undefined>(undefined);
  const [atAGlanceDefaultValues, setAtAGlanceDefaultValues] = useState<FormAtAGlanceData | undefined>(undefined);
  const [ourServicesDefaultValues, setOurServicesDefaultValues] = useState<FormOurServicesData | undefined>(undefined);
  const [aboutUsDefaultValues, setAboutUsDefaultValues] = useState<FormAboutUsData | undefined>(undefined);

  const [heroOldDefaultValues, setHeroOldDefaultValues] = useState<FormHeroData | undefined>(undefined);
  const [atAGlanceOldDefaultValues, setAtAGlanceOldDefaultValues] = useState<FormAtAGlanceData | undefined>(undefined);
  const [ourServicesOldDefaultValues, setOurServicesOldDefaultValues] = useState<FormOurServicesData | undefined>(
    undefined
  );
  const [aboutUsOldDefaultValues, setAboutUsOldDefaultValues] = useState<FormAboutUsData | undefined>(undefined);
  const [isHandlePreviewLoading, setIsHandlePreviewLoading] = useState(false);

  // This hook needs to call child methods in order to get the latest values from each child form
  // This approach should not be replicated in other pages, an exception was made here because of the large number of forms and fields
  // More information in the project standards https://www.notion.so/kana-earth/TypeScript-React-9c0cde0bbf07429ca4d84d5ca9f388b5#0582203770284f33b013224f58b46675
  const heroFormRef = useRef<MarketingAssetsSubmitHandler>(null);
  const atAGlanceFormRef = useRef<MarketingAssetsSubmitHandler>(null);
  const ourServicesFormRef = useRef<MarketingAssetsSubmitHandler>(null);
  const aboutUsFormRef = useRef<MarketingAssetsSubmitHandler>(null);

  const fetchListingPreviewData = useCallback(async () => {
    if (currentDevelopmentManagerUuid) {
      const organisationRes = await getDevelopmentManagerDetails({
        developmentManagerUuid: currentDevelopmentManagerUuid,
      });

      if (organisationRes.status === Status.Success && organisationRes.data) {
        if (organisationRes.data.listing?.rowVersion) {
          setListingRowVersion(organisationRes.data.listing.rowVersion);
        }

        const heroValuesOld: FormHeroData = {
          enabled: organisationRes.data?.listing?.content?.hero?.enabled
            ? organisationRes.data?.listing?.content?.hero?.enabled
            : false,
          title: organisationRes.data?.listing?.content?.hero?.title,
          subtitle: organisationRes.data?.listing?.content?.hero?.subtitle,
          imageFileUuid: organisationRes.data?.listing?.content?.hero?.imageFileUuid,
          imageFileUrl: organisationRes.data?.listingFiles?.find(
            (x) => x.uuid === organisationRes.data?.listing?.content?.hero?.imageFileUuid
          )?.file?.url,
        };
        const atAGlanceValuesOld: FormAtAGlanceData = {
          enabled:
            organisationRes.data?.listing?.content?.atAGlance?.enabled !== undefined
              ? organisationRes.data?.listing?.content?.atAGlance?.enabled
              : true,
        };
        const ourServicesValuesOld: FormOurServicesData = {
          enabled: organisationRes.data?.listing?.content?.ourServices?.enabled
            ? organisationRes.data?.listing?.content?.ourServices?.enabled
            : false,
          part1: organisationRes.data?.listing?.content?.ourServices?.part1
            ? {
                content: organisationRes.data?.listing?.content?.ourServices?.part1.content,
                title: organisationRes.data?.listing?.content?.ourServices?.part1.title,
                imageFileUuid: organisationRes.data?.listing?.content?.ourServices?.part1.imageFileUuid,
                imageFileUrl: organisationRes.data?.listingFiles?.find(
                  (x) => x.uuid === organisationRes.data?.listing?.content?.ourServices?.part1.imageFileUuid
                )?.file?.url,
              }
            : undefined,
          part2: organisationRes.data?.listing?.content?.ourServices?.part2
            ? {
                content: organisationRes.data?.listing?.content?.ourServices?.part2.content,
                title: organisationRes.data?.listing?.content?.ourServices?.part2.title,
                imageFileUuid: organisationRes.data?.listing?.content?.ourServices?.part2.imageFileUuid,
                imageFileUrl: organisationRes.data?.listingFiles?.find(
                  (x) => x.uuid === organisationRes.data?.listing?.content?.ourServices?.part2.imageFileUuid
                )?.file?.url,
              }
            : undefined,
          part3: organisationRes.data?.listing?.content?.ourServices?.part3
            ? {
                content: organisationRes.data?.listing?.content?.ourServices?.part3.content,
                title: organisationRes.data?.listing?.content?.ourServices?.part3.title,
                imageFileUuid: organisationRes.data?.listing?.content?.ourServices?.part3.imageFileUuid,
                imageFileUrl: organisationRes.data?.listingFiles?.find(
                  (x) => x.uuid === organisationRes.data?.listing?.content?.ourServices?.part3.imageFileUuid
                )?.file?.url,
              }
            : undefined,
        };
        const aboutUsValuesOld: FormAboutUsData = {
          enabled: organisationRes.data?.listing?.content?.aboutUs?.enabled
            ? organisationRes.data?.listing?.content?.aboutUs?.enabled
            : false,
          content: organisationRes.data?.listing?.content?.aboutUs?.content,
          imageFileUuid: organisationRes.data?.listing?.content?.aboutUs?.imageFileUuid,
          imageFileUrl: organisationRes.data?.listingFiles?.find(
            (x) => x.uuid === organisationRes.data?.listing?.content?.aboutUs?.imageFileUuid
          )?.file?.url,
        };

        setHeroOldDefaultValues(heroValuesOld);
        setAtAGlanceOldDefaultValues(atAGlanceValuesOld);
        setOurServicesOldDefaultValues(ourServicesValuesOld);
        setAboutUsOldDefaultValues(aboutUsValuesOld);
      }

      if (previewUuid) {
        const res = await getOrganisationListingPreview({
          previewUuid,
        });
        if (res.status === Status.Success && res.data) {
          const heroValues: FormHeroData = {
            enabled: false,
            title: res.data?.content?.hero?.title,
            subtitle: res.data?.content?.hero?.subtitle,
            imageFileUuid: res.data?.content?.hero?.imageFileUuid,
            imageFileUrl: res.data?.listingFiles?.find((x) => x.uuid === res.data?.content?.hero?.imageFileUuid)?.file
              ?.url,
          };

          if (res.data?.content?.hero?.enabled !== undefined) {
            heroValues.enabled = res.data?.content?.hero?.enabled;
          }

          setHeroDefaultValues(heroValues);

          const atAGlanceValues: FormAtAGlanceData = {
            enabled: true,
          };

          if (res.data?.content?.atAGlance?.enabled !== undefined) {
            atAGlanceValues.enabled = res.data?.content?.atAGlance?.enabled;
          }

          setAtAGlanceDefaultValues(atAGlanceValues);

          const ourServicesValues: FormOurServicesData = {
            enabled: false,
            part1: res.data?.content?.ourServices?.part1
              ? {
                  content: res.data?.content?.ourServices?.part1?.content,
                  title: res.data?.content?.ourServices?.part1?.title,
                  imageFileUuid: res.data?.content?.ourServices?.part1?.imageFileUuid,
                  imageFileUrl: res.data?.listingFiles?.find(
                    (x) => x.uuid === res.data?.content?.ourServices?.part1.imageFileUuid
                  )?.file?.url,
                }
              : undefined,
            part2: res.data?.content?.ourServices?.part2
              ? {
                  content: res.data?.content?.ourServices?.part2.content,
                  title: res.data?.content?.ourServices?.part2.title,
                  imageFileUuid: res.data?.content?.ourServices?.part2.imageFileUuid,
                  imageFileUrl: res.data?.listingFiles?.find(
                    (x) => x.uuid === res.data?.content?.ourServices?.part2.imageFileUuid
                  )?.file?.url,
                }
              : undefined,
            part3: res.data?.content?.ourServices?.part3
              ? {
                  content: res.data?.content?.ourServices?.part3.content,
                  title: res.data?.content?.ourServices?.part3.title,
                  imageFileUuid: res.data?.content?.ourServices?.part3.imageFileUuid,
                  imageFileUrl: res.data?.listingFiles?.find(
                    (x) => x.uuid === res.data?.content?.ourServices?.part3.imageFileUuid
                  )?.file?.url,
                }
              : undefined,
          };

          if (res.data?.content?.ourServices?.enabled !== undefined) {
            ourServicesValues.enabled = res.data?.content?.ourServices?.enabled;
          }

          setOurServicesDefaultValues(ourServicesValues);

          const aboutUsValues: FormAboutUsData = {
            enabled: false,
            content: res.data?.content?.aboutUs?.content,
            imageFileUuid: res.data?.content?.aboutUs?.imageFileUuid,
            imageFileUrl: res.data?.listingFiles?.find((x) => x.uuid === res.data?.content?.aboutUs?.imageFileUuid)
              ?.file?.url,
          };

          if (res.data?.content?.aboutUs?.enabled !== undefined) {
            aboutUsValues.enabled = res.data?.content?.aboutUs?.enabled;
          }

          setAboutUsDefaultValues(aboutUsValues);
        }
      }
    }
  }, []);

  const fetchOrganisationData = useCallback(async () => {
    if (currentDevelopmentManagerUuid) {
      const res = await getDevelopmentManagerDetails({
        developmentManagerUuid: currentDevelopmentManagerUuid,
      });

      if (res.status === Status.Success && res.data) {
        if (res.data.listing?.rowVersion) {
          setListingRowVersion(res.data.listing.rowVersion);
        }

        const heroValues: FormHeroData = {
          enabled: res.data.listing?.content?.hero?.enabled ? res.data.listing?.content?.hero?.enabled : false,
          title: res.data.listing?.content?.hero?.title,
          subtitle: res.data.listing?.content?.hero?.subtitle,
          imageFileUuid: res.data.listing?.content?.hero?.imageFileUuid,
          imageFileUrl: res.data?.listingFiles?.find((x) => x.uuid === res.data?.listing?.content?.hero?.imageFileUuid)
            ?.file?.url,
        };

        if (res.data?.listing?.content?.hero?.enabled !== undefined) {
          heroValues.enabled = res.data?.listing?.content.hero.enabled;
        }

        setHeroDefaultValues(heroValues);
        setHeroOldDefaultValues(heroValues);

        const atAGlanceValues: FormAtAGlanceData = {
          enabled: true,
        };

        if (res.data?.listing?.content?.atAGlance?.enabled !== undefined) {
          atAGlanceValues.enabled = res.data?.listing?.content?.atAGlance?.enabled;
        }

        setAtAGlanceDefaultValues(atAGlanceValues);
        setAtAGlanceOldDefaultValues(atAGlanceValues);

        const ourServicesValues: FormOurServicesData = {
          enabled: res.data?.listing?.content?.ourServices?.enabled
            ? res.data?.listing?.content?.ourServices?.enabled
            : false,
          part1: res.data?.listing?.content?.ourServices?.part1
            ? {
                content: res.data?.listing?.content.ourServices.part1.content,
                title: res.data?.listing?.content.ourServices.part1.title,
                imageFileUuid: res.data?.listing?.content.ourServices.part1.imageFileUuid,
                imageFileUrl: res.data?.listingFiles?.find(
                  (x) => x.uuid === res.data?.listing?.content?.ourServices.part1.imageFileUuid
                )?.file?.url,
              }
            : undefined,
          part2: res.data?.listing?.content?.ourServices?.part2
            ? {
                content: res.data?.listing?.content.ourServices.part2.content,
                title: res.data?.listing?.content.ourServices.part2.title,
                imageFileUuid: res.data?.listing?.content.ourServices.part2.imageFileUuid,
                imageFileUrl: res.data?.listingFiles?.find(
                  (x) => x.uuid === res.data?.listing?.content?.ourServices.part2.imageFileUuid
                )?.file?.url,
              }
            : undefined,
          part3: res.data?.listing?.content?.ourServices?.part3
            ? {
                content: res.data?.listing?.content.ourServices.part3.content,
                title: res.data?.listing?.content.ourServices.part3.title,
                imageFileUuid: res.data?.listing?.content.ourServices.part3.imageFileUuid,
                imageFileUrl: res.data?.listingFiles?.find(
                  (x) => x.uuid === res.data?.listing?.content?.ourServices.part3.imageFileUuid
                )?.file?.url,
              }
            : undefined,
        };

        if (res.data?.listing?.content?.ourServices?.enabled !== undefined) {
          ourServicesValues.enabled = res.data?.listing?.content.ourServices.enabled;
        }

        setOurServicesDefaultValues(ourServicesValues);
        setOurServicesOldDefaultValues(ourServicesValues);

        const aboutUsValues: FormAboutUsData = {
          enabled: res.data?.listing?.content?.aboutUs?.enabled ? res.data?.listing?.content?.aboutUs?.enabled : false,
          content: res.data?.listing?.content?.aboutUs?.content,
          imageFileUuid: res.data?.listing?.content?.aboutUs?.imageFileUuid,
          imageFileUrl: res.data?.listingFiles?.find(
            (x) => x.uuid === res.data?.listing?.content?.aboutUs?.imageFileUuid
          )?.file?.url,
        };

        if (res.data?.listing?.content?.aboutUs?.enabled !== undefined) {
          aboutUsValues.enabled = res.data?.listing?.content.aboutUs.enabled;
        }

        setAboutUsDefaultValues(aboutUsValues);
        setAboutUsOldDefaultValues(aboutUsValues);
      }
    }
  }, []);

  useEffect(() => {
    if (searchParams.get("origin") === Origin.DevelopmentManagerDetailsPreview) {
      fetchListingPreviewData().then(() => setDataIsLoading(false));
    } else {
      fetchOrganisationData().then(() => setDataIsLoading(false));
    }
  }, [fetchListingPreviewData, fetchOrganisationData]);

  const getCurrentFormsData = (formWhichWasSaved: string): string => {
    let hero;
    let atAGlance;
    let aboutUs;
    let ourServices;

    if (heroFormRef.current?.getHeroSubmittedData) {
      hero = heroFormRef.current.getHeroSubmittedData();
    }

    if (atAGlanceFormRef.current?.getAtAGlanceSubmittedData) {
      atAGlance = atAGlanceFormRef.current.getAtAGlanceSubmittedData();
    }

    if (ourServicesFormRef.current?.getOurServicesSubmittedData) {
      ourServices = ourServicesFormRef.current.getOurServicesSubmittedData();
    }

    if (aboutUsFormRef.current?.getAboutUsSubmittedData) {
      aboutUs = aboutUsFormRef.current.getAboutUsSubmittedData();
    }

    switch (formWhichWasSaved) {
      case FormNames.Hero: {
        if (heroFormRef.current?.getHeroData) {
          hero = heroFormRef.current.getHeroData();
        }
        break;
      }
      case FormNames.AtAGlance: {
        if (atAGlanceFormRef.current?.getAtAGlanceData) {
          atAGlance = atAGlanceFormRef.current.getAtAGlanceData();
        }
        break;
      }
      case FormNames.OurServices: {
        if (ourServicesFormRef.current?.getOurServicesData) {
          ourServices = ourServicesFormRef.current.getOurServicesData();
        }
        break;
      }
      case FormNames.AboutUs: {
        if (aboutUsFormRef.current?.getAboutUsData) {
          aboutUs = aboutUsFormRef.current.getAboutUsData();
        }
        break;
      }
      default: {
        logError({ error: `${formWhichWasSaved} is not a valid form` });
      }
    }

    return JSON.stringify(
      recursivelyRemoveEmptyStrings({
        hero,
        atAGlance,
        ourServices,
        aboutUs,
      })
    );
  };

  const handlePreview = useIsLoadingWrapper(async (): Promise<void> => {
    if (currentDevelopmentManagerUuid) {
      setPreviewMode(true);

      let hero;
      let atAGlance;
      let aboutUs;
      let ourServices;

      if (heroFormRef.current?.getHeroData) {
        hero = heroFormRef.current.getHeroData();
      }

      if (atAGlanceFormRef.current?.getAtAGlanceData) {
        atAGlance = atAGlanceFormRef.current.getAtAGlanceData();
      }

      if (ourServicesFormRef.current?.getOurServicesData) {
        ourServices = ourServicesFormRef.current.getOurServicesData();
      }

      if (aboutUsFormRef.current?.getAboutUsData) {
        aboutUs = aboutUsFormRef.current.getAboutUsData();
      }

      const res = await saveOrganisationListingPreview({
        objectUuid: currentDevelopmentManagerUuid,
        objectType: "DevelopmentManager",
        content: JSON.stringify(
          recursivelyRemoveEmptyStrings({
            hero,
            atAGlance,
            ourServices,
            aboutUs,
          })
        ),
      });

      if (res.status === Status.Success && res.data?.previewUuid) {
        navigate(getPublicDevelopmentManagerDetailsPreviewRoute(res.data.previewUuid));
      }
      if (res.status === Status.Error && res.errors) {
        const previewErrors: {
          hero: ServiceError[];
          ourServices: ServiceError[];
          aboutUs: ServiceError[];
        } = {
          hero: [],
          ourServices: [],
          aboutUs: [],
        };

        res.errors.forEach((error) => {
          switch (true) {
            case error.path?.includes(FormNames.Hero): {
              previewErrors.hero.push(error);
              break;
            }
            case error.path?.includes(FormNames.OurServices): {
              previewErrors.ourServices.push(error);
              break;
            }
            case error.path?.includes(FormNames.AboutUs): {
              previewErrors.aboutUs.push(error);
              break;
            }
            default: {
              logError({ error: `No form could be found for ${error.path}` });
            }
          }
        });

        if (heroFormRef.current?.setHeroErrors) {
          heroFormRef.current.setHeroErrors(previewErrors.hero);
        }
        if (ourServicesFormRef.current?.setOurServicesErrors) {
          ourServicesFormRef.current?.setOurServicesErrors(previewErrors.ourServices);
        }
        if (aboutUsFormRef.current?.setAboutUsErrors) {
          aboutUsFormRef.current?.setAboutUsErrors(previewErrors.aboutUs);
        }
      }
    }
  }, setIsHandlePreviewLoading);

  return {
    heroFormRef,
    ourServicesFormRef,
    atAGlanceFormRef,
    aboutUsFormRef,
    dataIsLoading,
    hasUnsavedChanges,
    listingRowVersion,
    setListingRowVersion,
    dispatch,
    heroDefaultValues,
    atAGlanceDefaultValues,
    ourServicesDefaultValues,
    aboutUsDefaultValues,
    heroOldDefaultValues,
    atAGlanceOldDefaultValues,
    ourServicesOldDefaultValues,
    aboutUsOldDefaultValues,
    isHandlePreviewLoading,
    handlePreview,
    previewMode,
    getCurrentFormsData,
    hasPermission,
    objectUuid: currentDevelopmentManagerUuid || "",
    objectType: OrganisationTypeConstants.DEVELOPMENT_MANAGER,
    objectKey: "developmentManagerUuid",
    updateListing: updateDevelopmentManagerListing,
  };
};
